import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * 어드민 라이선스 관리 — N슬롯 모델.
 *
 * 1) 현재 활성 라이선스 정보 (만료/feature/HWID).
 * 2) 다음 라이선스 큐 (pending) — "지금 활성화" / "폐기" 버튼.
 * 3) 새 라이선스 등록 (challenge → install) — Licensing.tsx 와 동일 흐름.
 *    active 가 이미 있으면 install 결과는 자동으로 pending 으로 들어감.
 */
export function AdminLicenses() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['license-status'],
    queryFn: api.licenseStatus,
    refetchOnWindowFocus: false,
  });

  const [licenseKey, setLicenseKey] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);

  const challengeMut = useMutation({
    mutationFn: () => api.licenseChallenge({ licenseKey: licenseKey.trim().toUpperCase() }),
    onSuccess: (res) => setChallenge(res.challenge),
  });

  const installMut = useMutation({
    mutationFn: () => api.licenseInstall({ token: token.trim() }),
    onSuccess: async () => {
      setToken('');
      setChallenge(null);
      setLicenseKey('');
      await qc.invalidateQueries({ queryKey: ['license-status'] });
    },
  });

  const activateMut = useMutation({
    mutationFn: (slotId: string) => api.licenseSlotActivate(slotId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['license-status'] }),
  });

  const retireMut = useMutation({
    mutationFn: (slotId: string) => api.licenseSlotRetire(slotId, 'manual'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['license-status'] }),
  });

  async function copyChallenge() {
    if (!challenge) return;
    await navigator.clipboard.writeText(challenge);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const data = status.data;

  return (
    <div className="p-6 max-w-4xl space-y-8">
      <header>
        <h1 className="text-xl font-semibold">라이선스</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          현재 활성 라이선스 + 다음 라이선스 큐. 만료 직전 다음 라이선스를 미리 등록해두면 자동으로
          전환됩니다.
        </p>
      </header>

      {/* 현재 활성 */}
      <section className="rounded-lg border bg-card p-5 space-y-3">
        <h2 className="text-base font-semibold">활성 라이선스</h2>
        {!data ? (
          <p className="text-sm text-muted-foreground">로딩…</p>
        ) : !data.enforced ? (
          <p className="text-sm text-muted-foreground">
            라이선스 enforce 가 꺼져 있습니다 (INNERMATE_LICENSE_ENFORCE 미설정). 모든 기능 무제한.
          </p>
        ) : data.active ? (
          <div className="space-y-2 text-sm">
            <div>
              상태:{' '}
              {data.valid ? (
                <span className="text-success font-medium">유효</span>
              ) : (
                <span className="text-destructive font-medium">{data.reason ?? '무효'}</span>
              )}
            </div>
            {data.warning && (
              <div className="text-sm text-destructive">⚠ {data.warning}</div>
            )}
            <div>
              만료:{' '}
              <span className="font-mono">
                {data.active.expiresAt ? new Date(data.active.expiresAt).toLocaleString() : '없음'}
              </span>
            </div>
            <div>features: {data.features.join(', ') || '없음'}</div>
            <div className="text-xs text-muted-foreground">
              슬롯 ID <code className="font-mono">{data.active.id}</code> · install{' '}
              <code className="font-mono">
                {new Date(data.active.installedAt).toLocaleDateString()}
              </code>
            </div>
          </div>
        ) : (
          <p className="text-sm text-destructive">
            활성 라이선스 없음 — 사용자가 접속하면 라이선스 활성화 화면이 표시됩니다.
          </p>
        )}
      </section>

      {/* 다음 라이선스 큐 */}
      <section className="rounded-lg border bg-card p-5 space-y-3">
        <h2 className="text-base font-semibold">
          다음 라이선스 큐 ({data?.pending.length ?? 0}개)
        </h2>
        {!data || data.pending.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            대기 중인 라이선스 없음. 만료 직전에 새 라이선스를 미리 등록해두면 자동 전환됩니다.
          </p>
        ) : (
          <div className="space-y-2">
            {data.pending.map((slot) => (
              <div
                key={slot.id}
                className="flex items-center justify-between gap-3 rounded border p-3 text-sm"
              >
                <div className="space-y-1">
                  <div>
                    만료:{' '}
                    <span className="font-mono">
                      {slot.expiresAt ? new Date(slot.expiresAt).toLocaleString() : '없음'}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    install{' '}
                    {new Date(slot.installedAt).toLocaleString()} · 슬롯{' '}
                    <code className="font-mono">{slot.id.slice(0, 8)}…</code>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => activateMut.mutate(slot.id)}
                    disabled={activateMut.isPending}
                    className="px-3 py-1.5 bg-primary text-primary-foreground text-xs rounded hover:opacity-90 disabled:opacity-50"
                  >
                    지금 활성화
                  </button>
                  <button
                    type="button"
                    onClick={() => retireMut.mutate(slot.id)}
                    disabled={retireMut.isPending}
                    className="px-3 py-1.5 border text-xs rounded hover:bg-muted disabled:opacity-50"
                  >
                    폐기
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {activateMut.error && (
          <p className="text-sm text-destructive">
            활성화 실패: {(activateMut.error as Error).message}
          </p>
        )}
        {retireMut.error && (
          <p className="text-sm text-destructive">
            폐기 실패: {(retireMut.error as Error).message}
          </p>
        )}
      </section>

      {/* 새 라이선스 등록 */}
      <section className="rounded-lg border bg-card p-5 space-y-4">
        <h2 className="text-base font-semibold">새 라이선스 등록</h2>
        <p className="text-sm text-muted-foreground">
          본사에서 받은 라이선스 키 → 활성화 코드 생성 → 본사 사이트 → 토큰 설치. 활성 라이선스가
          있으면 자동으로 큐 마지막에 들어갑니다.
        </p>

        <div className="space-y-2">
          <label className="text-sm font-medium">라이선스 키</label>
          <input
            type="text"
            value={licenseKey}
            onChange={(e) => setLicenseKey(e.target.value)}
            placeholder="INMT-XXXX-XXXX-XXXX-XXXX-XXXX"
            className="w-full px-3 py-2 font-mono text-sm border rounded focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="button"
            onClick={() => challengeMut.mutate()}
            disabled={!licenseKey.trim() || challengeMut.isPending}
            className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded disabled:opacity-50"
          >
            {challengeMut.isPending ? '생성 중…' : '활성화 코드 생성'}
          </button>
          {challengeMut.error && (
            <p className="text-sm text-destructive">
              {(challengeMut.error as Error).message}
            </p>
          )}
        </div>

        {challenge && (
          <div className="space-y-2 pt-2">
            <label className="text-sm font-medium">활성화 코드 (본사 사이트에 붙여넣기)</label>
            <textarea
              value={challenge}
              readOnly
              rows={4}
              spellCheck={false}
              className="w-full px-3 py-2 font-mono text-xs bg-muted border rounded"
            />
            <button
              type="button"
              onClick={copyChallenge}
              className="px-3 py-1.5 border text-xs rounded hover:bg-muted"
            >
              {copied ? '복사됨!' : '클립보드에 복사'}
            </button>
          </div>
        )}

        {challenge && (
          <div className="space-y-2 pt-2 border-t">
            <label className="text-sm font-medium">활성화 토큰</label>
            <textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              rows={4}
              spellCheck={false}
              placeholder="eyJhbGciOiJFZERTQSIs..."
              className="w-full px-3 py-2 font-mono text-xs border rounded focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="button"
              onClick={() => installMut.mutate()}
              disabled={!token.trim() || installMut.isPending}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded disabled:opacity-50"
            >
              {installMut.isPending ? '설치 중…' : '설치'}
            </button>
            {installMut.error && (
              <p className="text-sm text-destructive">
                {(installMut.error as Error).message}
              </p>
            )}
            {installMut.data?.slot === 'pending' && (
              <p className="text-sm text-success">
                다음 큐에 등록되었습니다 ({installMut.data.mode} 모드 유지). 현재 라이선스 만료 시
                자동 전환됩니다.
              </p>
            )}
            {installMut.data?.slot === 'active' && (
              <p className="text-sm text-success">활성 라이선스로 설정되었습니다.</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
